import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, UserPlus, Globe, Mail, AlertCircle } from 'lucide-react'
import clsx from 'clsx'
import Modal from './Modal'
import { useAssociableUsers, useAssociateExistingUser } from '../../hooks/useUsers'

const roleOptions = [
  { value: 'Owner', label: 'Owner' },
  { value: 'Admin', label: 'Admin' },
  { value: 'Operator', label: 'Operator' },
  { value: 'Viewer', label: 'Viewer' },
]

export default function AssociateExistingUserModal({ isOpen, onClose }) {
  const { t } = useTranslation()
  const { data: users, isLoading, error } = useAssociableUsers(isOpen)
  const associate = useAssociateExistingUser()

  const [searchTerm, setSearchTerm] = useState('')
  const [role, setRole] = useState('Viewer')
  const [pendingId, setPendingId] = useState(null)
  const [feedback, setFeedback] = useState(null) // { type: 'success' | 'error', text }

  const filtered = (users || []).filter((u) => {
    const term = searchTerm.toLowerCase()
    return (
      u.name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term)
    )
  })

  const handleAssociate = async (u) => {
    setPendingId(u.userId)
    setFeedback(null)
    try {
      await associate.mutateAsync({ userId: u.userId, role })
      setFeedback({ type: 'success', text: t('users.associateExisting.success', { name: u.name || u.email }) })
    } catch (err) {
      const message = err?.response?.data?.message || t('users.associateExisting.error')
      setFeedback({ type: 'error', text: message })
    } finally {
      setPendingId(null)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('users.associateExisting.title')}>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          {t('users.associateExisting.subtitle')}
        </p>

        {/* Role + busca */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('users.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 input"
            />
          </div>
          <div className="sm:w-48">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="input w-full"
              title={t('users.associateExisting.roleHint')}
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {t('users.associateExisting.roleLabel', { role: option.label })}
                </option>
              ))}
            </select>
          </div>
        </div>

        {feedback && (
          <div
            className={clsx(
              'text-sm rounded-lg px-3 py-2 border',
              feedback.type === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-red-50 border-red-200 text-red-800',
            )}
          >
            {feedback.text}
          </div>
        )}

        {/* Lista */}
        <div className="border border-gray-200 rounded-lg overflow-hidden max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-sm text-gray-600">{t('users.associateExisting.loading')}</div>
          ) : error ? (
            <div className="p-4 text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {t('users.associateExisting.error')}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-600">
              {searchTerm ? t('users.empty.none') : t('users.associateExisting.empty')}
            </div>
          ) : (
            <table className="w-full">
              <tbody className="divide-y divide-gray-200 bg-white">
                {filtered.map((u) => (
                  <tr key={u.userId} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-gray-900">
                          {u.name || t('users.nameFallback')}
                        </span>
                        {u.isGlobalAccess && (
                          <span className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-blue-100 text-blue-800 border border-blue-200">
                            <Globe className="w-3 h-3" />
                            {t('users.associateExisting.globalBadge')}
                          </span>
                        )}
                        {!u.isActive && (
                          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide bg-gray-100 text-gray-600 border border-gray-200">
                            {t('users.status.noAccessBadge')}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 flex items-center gap-1">
                        <Mail className="w-3 h-3 flex-shrink-0" />
                        {u.email}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleAssociate(u)}
                        disabled={associate.isPending}
                        className="btn btn-primary text-xs px-3 py-2"
                      >
                        <UserPlus className="w-4 h-4" />
                        {pendingId === u.userId
                          ? t('users.associateExisting.associating')
                          : t('users.associateExisting.associate')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            {t('users.modal.cancel')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
